import React, {FC, useCallback, useEffect} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {useEventHandler} from '../../../../libs/shared/core/src/helpers/react/hooks/useEventHandler';
import {TeammateModel} from '../../../../libs/shared/core/src/models/api/teammateModel';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {AppPanelId, NewPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Banner, BannerTypesEnum} from '../../../../libs/shared/ui/src/components/banners/banner';
import {BannerAction} from '../../../../libs/shared/ui/src/components/banners/bannerAction';
import {BannerCloseAction} from '../../../../libs/shared/ui/src/components/banners/bannerCloseAction';
import {BannerContent} from '../../../../libs/shared/ui/src/components/banners/bannerContent';
import {BannerFooterActions} from '../../../../libs/shared/ui/src/components/banners/bannerFooterActions';
import {BannerPrioritiesEnum} from '../../../../libs/shared/ui/src/components/banners/bannersContext';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {useLinkNavigation} from '../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppDispatch} from '../../../helpers/appReduxHelpers';
import {buildTeammatePath} from '../../../helpers/modelHelpers';
import {IntegrationModel} from '../../../models/api/integrationModel';
import {LegacyApplicationModel} from '../../../models/api/legacyApplicationModel';
import {TeammatesFoldersEnum} from '../../../models/url/models/inboxesUrlModel';
import {hideBannerAction} from '../../../store/panels/app/banners/bannersActions';
import {
  ApplicationBannerState,
  ApplicationBannerTypesEnum,
} from '../../../store/panels/app/banners/bannersState';

/*
 * Props.
 */

interface ApplicationBannerProps {
  panelId: AppPanelId;
  bannerState: ApplicationBannerState;
  integration?: IntegrationModel;
  application?: LegacyApplicationModel;
  user: TeammateModel;
  isInInboxesTab: boolean;
  isPinned: boolean;
  openSideBar: (newSelectedFrameId: string | undefined, panelId: NewPanelId) => void;
  pinPlugin: (arg: string) => void;
  frameId?: string;
}

/*
 * Style.
 */

const StyledBannerIcon = styled.div`
  grid-area: icon;
  margin: 2px 0;
`;

const StyledBannerCloseAction = styled(BannerCloseAction)`
  color: ${(p) => p.theme.greys.shade70};
`;

const StyledBannerContent = styled(BannerContent)`
  color: ${(p) => p.theme.greys.shade80};
`;

const StyledBannerText = styled.div`
  position: relative;
  grid-area: content;

  color: ${(p) => p.theme.greys.white};
  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.medium};
`;

const StyledBannerAction = styled(BannerAction)`
  color: ${(p) => p.theme.greys.white};
`;

/*
 * Component.
 */

export const ApplicationBanner: FC<ApplicationBannerProps> = (props) => {
  const {
    panelId,
    bannerState,
    openSideBar,
    pinPlugin,
    isInInboxesTab,
    user,
    frameId,
    integration,
    application,
    isPinned,
  } = props;
  const theme = useUserTheme();
  const name = integration?.name || application?.name;
  const isAppEnabled = Boolean(integration?.isEnabled || application?.isEnabled);
  const {navigateToLink} = useLinkNavigation();
  const dispatch = useAppDispatch();

  const onClose = useCallback(() => {
    dispatch(hideBannerAction(bannerState.uid));
  }, [bannerState.uid, dispatch, panelId]);

  useEffect(() => {
    // If we already have it pinned, we can remove the banner.
    if (isPinned) {
      onClose();
    }
    // If the app is no longer enabled, we can remove the banner.
    if (!isAppEnabled) {
      onClose();
    }
  }, [isPinned, isAppEnabled, onClose]);

  // We do not have an integration, application or frame id.
  // We required a frame id to be able to actually pin something to the sidebar.
  if ((!integration && !application) || !frameId || isPinned) {
    return null;
  }

  // The integration and application is not enabled this banner should no longer be visible.
  if (!isAppEnabled) {
    return null;
  }

  const onOpenClick = () => {
    if (frameId) {
      trackInterfaceEvent(InterfaceEventsEnum.APPS_SETTINGS_BANNER_OPEN_PLUGIN, {
        integration_id: integration?.id,
        integration_type: integration?.type,
        application_id: application?.id,
      });
      pinPlugin(frameId);
      openSideBar(frameId, panelId);

      // If the user is not in the inboxes tab, move them to the inboxes.
      if (!isInInboxesTab) {
        navigateToLink(buildTeammatePath(user.id, TeammatesFoldersEnum.INBOX));
      }
    }
    onClose();
  };

  const onCloseClick = () => {
    trackInterfaceEvent(InterfaceEventsEnum.APPS_SETTINGS_BANNER_CLOSE, {
      integration_id: integration?.id,
      integration_type: integration?.type,
      application_id: application?.id,
    });
    onClose();
  };

  if (bannerState.applicationType !== ApplicationBannerTypesEnum.APPLICATION_ENABLED) {
    return null;
  }

  return (
    <Banner
      bannerType={BannerTypesEnum.MULTILINE}
      color={theme.greys.shade90}
      priority={BannerPrioritiesEnum.TRANSIENT}
    >
      <StyledBannerCloseAction onClick={onCloseClick} />
      <StyledBannerIcon>
        <Icon name="integrationsFilledSmall" color={theme.greys.shade40} />
      </StyledBannerIcon>
      <StyledBannerContent>
        <StyledBannerText>
          <Trans id="application-enabled-banner-description">
            The {name} plugin has been enabled by a teammate. Pin it to your sidebar to begin using this
            plugin.
          </Trans>
        </StyledBannerText>
      </StyledBannerContent>
      <BannerFooterActions>
        <StyledBannerAction onClick={onOpenClick}>
          <Trans id="application-enabled-banner-action">OPEN PLUGIN</Trans>
        </StyledBannerAction>
      </BannerFooterActions>
      <ApplicationBannerViewTracking integration={integration} application={application} />
    </Banner>
  );
};

const ApplicationBannerViewTracking: FC<Pick<ApplicationBannerProps, 'integration' | 'application'>> = (
  props,
) => {
  const {integration, application} = props;

  const onView = useEventHandler(() => {
    if (!integration && !application) {
      return;
    }

    trackInterfaceEvent(InterfaceEventsEnum.APPS_SETTINGS_BANNER_VIEW, {
      integration_id: integration?.id,
      integration_type: integration?.type,
      application_id: application?.id,
    });
  });

  useEffect(() => {
    onView();
  }, [onView]);

  return null;
};
